import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {CampaignMembershipSnapshotDto} from "../../model/dto/entity/campaign-membership-snapshot.dto";

/**
 * Service for managing campaign membership snapshots.
 * Implements F-011: Campaign Persistence and History - Historical Snapshots.
 *
 * Provides methods for creating and querying point-in-time snapshots
 * of campaign membership for historical reporting and audit purposes.
 */
@Injectable({
    providedIn: 'root'
})
export class CampaignMembershipSnapshotService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/campaignMembershipSnapshots')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-camera';
    }

    /**
     * Creates a membership snapshot for historical reporting.
     * Captures the current state of campaign membership at a point in time.
     *
     * @param campaignId the campaign ID
     * @param description optional description of why the snapshot was taken
     */
    createSnapshot(campaignId: number, description?: string): Observable<CampaignMembershipSnapshotDto> {
        const params = description ? `?description=${encodeURIComponent(description)}` : '';
        return this.postGeneric<CampaignMembershipSnapshotDto>({}, `/campaign/${campaignId}/snapshot${params}`);
    }

    /**
     * Gets all membership snapshots for a campaign.
     * Returns snapshots in reverse chronological order (newest first).
     *
     * @param campaignId the campaign ID
     */
    getByCampaignId(campaignId: number): Observable<CampaignMembershipSnapshotDto[]> {
        return this.getGeneric<CampaignMembershipSnapshotDto[]>(`/byCampaign/${campaignId}`);
    }

    /**
     * Gets membership snapshots for a campaign within a date range.
     *
     * @param campaignId the campaign ID
     * @param startDate start of the date range
     * @param endDate end of the date range
     */
    getByCampaignIdAndDateRange(campaignId: number, startDate: Date, endDate: Date): Observable<CampaignMembershipSnapshotDto[]> {
        const startIso = startDate.toISOString();
        const endIso = endDate.toISOString();
        return this.getGeneric<CampaignMembershipSnapshotDto[]>(
            `/byCampaign/${campaignId}/byDateRange?startDate=${encodeURIComponent(startIso)}&endDate=${encodeURIComponent(endIso)}`
        );
    }

    /**
     * Gets the most recent membership snapshot for a campaign.
     *
     * @param campaignId the campaign ID
     */
    getLatestByCampaignId(campaignId: number): Observable<CampaignMembershipSnapshotDto> {
        return this.getGeneric<CampaignMembershipSnapshotDto>(`/byCampaign/${campaignId}/latest`);
    }
}
